import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Property } from '@/components/form/fields/select/SelectInput';
import { Form } from '@/components/form/Form';
import { ImageDetails, PropertyBag } from '@/services/api/contracts/catalog/types';
import { useCreateProduct, useProducts } from '@/services/api/hooks/product';
import { useRegions } from '@/services/api/hooks/region';
import { BasicBetterBest, useBasicBetterBestOptions } from '@/services/catalogue/useBasicBetterBest';
import { GBP } from '@dinero.js/currencies';
import { zodResolver } from '@hookform/resolvers/zod';
import { Value } from '@udecode/plate-common';
import { dinero } from 'dinero.js';
import { FC, PropsWithChildren, useState } from 'react';
import { useForm } from 'react-hook-form';
import LocalizedStrings from 'react-localization';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { Page } from '../../../layouts/Page';
import { PriceAndMarginForm, PriceAndMarginFormFields } from '../price/PriceAndMarginForm';
import CreateProductCustomerRestrictions from './customerRestrictions/create/CreateProductCustomerRestrictions';
import { ProcessedOrgTree } from '@/components/org/TenantRestrictionTree';
import { useProcessOrgTreeForProductRestrictions } from './customerRestrictions/useProcessOrgTreeForProductRestrictions';
import { useSaveProductRestrictions } from './customerRestrictions/useSaveProductRestrictions';
import { BaseProductProperties, ProductDescription, ProductForm, ProductSidebar } from './ProductForm';

const strings = new LocalizedStrings({
  en: {
    addNewProduct: 'Add new product',
    cancel: 'Cancel',
    createAndPublish: 'Create & publish',
    productCreated: 'Product {0} created'
  }
});

interface CreateProductProps {}

const CreateProductFormFields = PriceAndMarginFormFields.extend({
  productId: z.string(),
  description: z.custom<Value>(),
  propertyBag: PropertyBag,
  imageUri: ImageDetails.optional(),
  manufacturer: z.custom<Property>(),
  category: z.custom<Property>(),
  name: z.string(),
  basicBetterBest: z.custom<Property>(),
  modifier: z.string().optional(),
  region: z.custom<Property>(),
  isRateCardItem: z.boolean().optional(),
  triggerTypes: z.array(z.string()).optional().nullable(),
  bondedItemIds: z.array(z.string()).optional(),
  groupMaxQuantity: z.number().optional(),
  requiresSerialNumber: z.boolean().optional(),
  basePackForRequestType: z.string().optional()
});

type CreateProductFormFields = z.infer<typeof CreateProductFormFields>;

export const CreateProduct: FC<PropsWithChildren<CreateProductProps>> = () => {
  const bbbOptions = useBasicBetterBestOptions();
  const [products] = useProducts();

  const methods = useForm<CreateProductFormFields>({
    defaultValues: {
      price: dinero({ amount: 0, currency: GBP }),
      basicBetterBest: bbbOptions[0],
      isRateCardItem: false,
      bondedItemIds: [],
      groupMaxQuantity: 0,
      requiresSerialNumber: false,
      basePackForRequestType: 'None'
    },
    resolver: zodResolver(CreateProductFormFields)
  });

  const createProduct = useCreateProduct();
  const navigate = useNavigate();

  const orgTreeForProductRestrictions = useProcessOrgTreeForProductRestrictions();
  const onSaveProductRestrictions = useSaveProductRestrictions();
  const [productRestrictionOrgTree, setProductRestrictionOrgTree] =
    useState<ProcessedOrgTree[]>(orgTreeForProductRestrictions);
  const onConfirmProductRestrictions = async (orgTree: ProcessedOrgTree[]) => {
    setProductRestrictionOrgTree(orgTree);
  };

  const onSubmit = async (data: CreateProductFormFields) => {
    if (data.isRateCardItem && data.basePackForRequestType && data.basePackForRequestType !== 'None') {
      const existingBasePack = products?.find(
        p =>
          p.isRateCardItem &&
          p.region.id === data.region.id &&
          p.propertyBag.basePackForRequestType === data.basePackForRequestType
      );
      if (existingBasePack) {
        toast.error('A Base Pack for this request type already exists in this Region.');
        return;
      }
    }

    const price = data.price.toJSON();
    const propertyBagPayload: any = {
      properties: data.propertyBag,
      basicBetterBest: data.basicBetterBest.id as BasicBetterBest
    };

    if (data.isRateCardItem) {
      if (data.triggerTypes) propertyBagPayload.triggerTypes = data.triggerTypes;
      if (data.bondedItemIds) propertyBagPayload.bondedItemIds = data.bondedItemIds;
      if (data.groupMaxQuantity !== undefined) propertyBagPayload.groupMaxQuantity = data.groupMaxQuantity;
      if (data.requiresSerialNumber !== undefined) propertyBagPayload.requiresSerialNumber = data.requiresSerialNumber;
      if (data.basePackForRequestType && data.basePackForRequestType !== 'None') propertyBagPayload.basePackForRequestType = data.basePackForRequestType;
    }

    const product = await createProduct.mutateAsync({
      ...data,
      price: price.amount,
      categoryId: data.category.id,
      manufacturerId: data.manufacturer.id,
      imageUri: data.imageUri ? (Object.keys(data.imageUri).length > 0 ? data.imageUri : undefined) : undefined,
      propertyBag: propertyBagPayload,
      description: {
        value: data.description
      },
      min: 0,
      max: 10000000,
      modifier: data.modifier ? Number(data.modifier) : 0,
      regionId: data.region.id,
      isRateCardItem: data.isRateCardItem
    });
    toast.success(strings.formatString(strings.productCreated, product.name));

    const customerRestrictions = productRestrictionOrgTree.reduce((count, item: ProcessedOrgTree) => {
      // Count the number of checked customers in the current item
      const checkedCustomers = item.customers.filter(customer => customer.checked).length;
      // Add the number of checked customers to the total count
      return count + checkedCustomers;
    }, 0);

    if (customerRestrictions > 0) {
      await onSaveProductRestrictions(product.id, productRestrictionOrgTree);
    }

    navigate(`/configuratorCatalogue/${product.category.id}`);
  };

  const selectedRegionId = methods.watch('region.id');
  const [regions] = useRegions();
  const selectedRegion = regions.find(r => r.id === selectedRegionId);

  return (
    <Page className="flex">
      <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-1 flex-col overflow-hidden px-8 pt-8">
        <div className="mb-8 flex w-full items-center justify-between">
          <h2 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <span>{strings.addNewProduct}</span>
          </h2>
          <div className="flex gap-2">
            <Button type="button" onClick={() => navigate(-1)}>
              {strings.cancel}
            </Button>
            <Button variant={ButtonVariant.Primary} loading={createProduct.isLoading} type="submit">
              {strings.createAndPublish}
            </Button>
          </div>
        </div>
        <ProductForm>
          <ProductDescription />
          <ProductSidebar>
            <BaseProductProperties />
            {selectedRegion && (
              <div className="grid grid-cols-2 gap-2">
                <PriceAndMarginForm regionCode={selectedRegion.code} />
              </div>
            )}
            <CreateProductCustomerRestrictions
              orgTree={productRestrictionOrgTree}
              onConfirmCallback={onConfirmProductRestrictions}
            />
          </ProductSidebar>
        </ProductForm>
      </Form>
    </Page>
  );
};
